export type CrosswordDirection = 'ltr' | 'rtl' | 'ttb' | 'btt';

/** Legacy aliases still accepted from stored data. */
export type CrosswordDirectionInput = CrosswordDirection | 'across' | 'down' | string;

export interface CrosswordEntryInput {
  word: string;
  startRow: number;
  startCol: number;
  direction: CrosswordDirectionInput;
}

export function normalizeCrosswordDirection(raw: CrosswordDirectionInput | null | undefined): CrosswordDirection {
  const d = String(raw ?? 'ltr').trim().toLowerCase();
  if (d === 'rtl' || d === 'right-to-left' || d === 'righttoleft') return 'rtl';
  if (d === 'ttb' || d === 'down' || d === 'top-to-bottom' || d === 'toptobottom') return 'ttb';
  if (d === 'btt' || d === 'up' || d === 'bottom-to-top' || d === 'bottomtotop') return 'btt';
  // across / ltr / default
  return 'ltr';
}

export function crosswordDirectionDelta(direction: CrosswordDirectionInput): { dr: number; dc: number } {
  switch (normalizeCrosswordDirection(direction)) {
    case 'rtl':
      return { dr: 0, dc: -1 };
    case 'ttb':
      return { dr: 1, dc: 0 };
    case 'btt':
      return { dr: -1, dc: 0 };
    case 'ltr':
    default:
      return { dr: 0, dc: 1 };
  }
}

export function crosswordDirectionLabel(direction: CrosswordDirectionInput): string {
  switch (normalizeCrosswordDirection(direction)) {
    case 'rtl':
      return 'Right to left';
    case 'ttb':
      return 'Top to bottom';
    case 'btt':
      return 'Bottom to top';
    case 'ltr':
    default:
      return 'Left to right';
  }
}

/**
 * Normalize a single crossword character for comparison (Persian-friendly).
 * Must stay semantically aligned with InteractiveQuestionGrading.NormalizeCrosswordChar.
 */
export function normalizeCrosswordChar(ch: string): string {
  if (!ch) return '';
  const c = ch.charAt(0);
  // Zero-width / bidi marks must not occupy a cell (Farsi keyboard semi-space, BOM, etc.).
  if (
    c === '\u200B' ||
    c === '\u200C' ||
    c === '\u200D' ||
    c === '\uFEFF' ||
    c === '\u200E' ||
    c === '\u200F'
  ) {
    return '';
  }
  if (c === 'ي' || c === '\u06D2') return 'ی';
  if (c === 'ك') return 'ک';
  // Only upper-case Latin a–z; never toLocaleUpperCase (harmful for Persian).
  if (c >= 'a' && c <= 'z') return c.toUpperCase();
  return c;
}

/** Cells occupied by a crossword entry along its direction. */
export function crosswordEntryCells(
  startRow: number,
  startCol: number,
  word: string,
  direction: CrosswordDirectionInput
): Array<{ r: number; c: number }> {
  const { dr, dc } = crosswordDirectionDelta(direction);
  const len = String(word ?? '').length;
  const cells: Array<{ r: number; c: number }> = [];
  for (let i = 0; i < len; i++) {
    cells.push({ r: startRow + i * dr, c: startCol + i * dc });
  }
  return cells;
}

export function persianFriendlyCharEquals(a: string, b: string): boolean {
  if (!a || !b) return false;
  return normalizeCrosswordChar(a) === normalizeCrosswordChar(b);
}

/**
 * Build row-major mask: '#' for non-playable, uppercase letter for solution cells.
 */
export function buildCrosswordMask(
  rows: number,
  cols: number,
  entries: CrosswordEntryInput[]
): string {
  if (rows <= 0 || cols <= 0) return '';
  const grid: (string | null)[][] = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => null)
  );

  for (const entry of entries) {
    const word = String(entry.word ?? '').trim();
    if (!word) continue;
    const { dr, dc } = crosswordDirectionDelta(entry.direction);
    for (let i = 0; i < word.length; i++) {
      const r = entry.startRow + i * dr;
      const c = entry.startCol + i * dc;
      if (r < 0 || r >= rows || c < 0 || c >= cols) continue;
      const ch = normalizeCrosswordChar(word[i]!);
      if (!ch) continue;
      if (grid[r][c] != null && grid[r][c] !== ch) continue;
      grid[r][c] = ch;
    }
  }

  let out = '';
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      out += grid[r][c] ?? '#';
    }
  }
  return out;
}

/** Blank learner answer: '#' blocks, '.' playable empties. */
export function buildCrosswordBlankAnswer(
  rows: number,
  cols: number,
  entries: CrosswordEntryInput[]
): string {
  const mask = buildCrosswordMask(rows, cols, entries);
  return mask
    .split('')
    .map((ch) => (ch === '#' ? '#' : '.'))
    .join('');
}

/**
 * Returns an error string if entries overlap with conflicting letters or go out of bounds; otherwise null.
 */
export function validateCrosswordEntries(
  rows: number,
  cols: number,
  entries: CrosswordEntryInput[]
): string | null {
  if (rows <= 0 || cols <= 0) return 'Grid dimensions must be positive.';
  if (cols > 10) return 'Columns cannot exceed 10.';
  const grid: (string | null)[][] = Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => null)
  );

  for (const entry of entries) {
    const word = String(entry.word ?? '').trim();
    if (!word) continue;
    const { dr, dc } = crosswordDirectionDelta(entry.direction);
    for (let i = 0; i < word.length; i++) {
      const r = entry.startRow + i * dr;
      const c = entry.startCol + i * dc;
      if (r < 0 || r >= rows || c < 0 || c >= cols) {
        return `Entry "${word}" exceeds grid bounds at (${r},${c}).`;
      }
      const ch = normalizeCrosswordChar(word[i]!);
      if (!ch) continue;
      const existing = grid[r][c];
      if (existing != null && existing !== ch) {
        return `Overlap conflict at (${r},${c}) for entry "${word}".`;
      }
      grid[r][c] = ch;
    }
  }
  return null;
}
